import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../config/api";

// Function to construct proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // If imagePath is already a full URL, return it as is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // Construct full URL using the base URL from the API config
  // Get the base URL and remove the /api part to get the root
  const baseUrl =
    api.defaults.baseURL?.replace("/api", "") || "https://skillsiksha.in";

  // Always prepend /storage/ to the image path
  if (imagePath.startsWith("/storage/")) {
    return `${baseUrl}${imagePath}`;
  } else if (imagePath.startsWith("storage/")) {
    return `${baseUrl}/${imagePath}`;
  } else {
    // Prepend /storage/ to any relative path
    return `${baseUrl}/storage/${imagePath}`;
  }
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [previousBlog, setPreviousBlog] = useState(null);
  const [nextBlog, setNextBlog] = useState(null);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  // Toggle a global loading class on <body> so header/footer can react (logo spin, spacing preserved)
  useEffect(() => {
    if (loading) {
      document.body.classList.add('loading-active');
    } else {
      document.body.classList.remove('loading-active');
    }
    return () => {
      document.body.classList.remove('loading-active');
    };
  }, [loading]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const response = await api.get(`/blogs/${slug}`);
      const data = response.data;

      if (data.success) {
        setBlog(data.data);
        
        // Debugging: log the related blogs data
        

        // Fetch related blogs
        if (data.data.related_blogs && Array.isArray(data.data.related_blogs) && data.data.related_blogs.length > 0) {
          setRelatedBlogs(data.data.related_blogs);
          
        } else if (data.data.related_posts && Array.isArray(data.data.related_posts) && data.data.related_posts.length > 0) {
          // If we have related post IDs, fetch the actual blog data for each
          
          fetchRelatedBlogsByIds(data.data.related_posts);
        } else {
          
          setRelatedBlogs([]);
        }

        // Fetch previous and next blogs
        if (data.data.previous_blog) {
          setPreviousBlog(data.data.previous_blog);
        }

        if (data.data.next_blog) {
          setNextBlog(data.data.next_blog);
        }
      } else {
        setError("Blog not found");
      }
    } catch (err) {
      
      if (err.response && err.response.status === 404) {
        setError("Blog not found");
      } else if (err.response) {
        // Server responded with error status
        setError(`Error loading blog: ${err.response.status} - ${err.response.statusText}`);
      } else if (err.request) {
        // Request was made but no response received
        setError("Network error - please check your connection");
      } else {
        // Something else happened
        setError("An error occurred while fetching the blog");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedBlogsByIds = async (relatedIds) => {
    try {
      // Create an array of promises to fetch each related blog by ID
      const promises = relatedIds.map(id => 
        api.get(`/blogs/${id}`).catch(err => {
         
          return null; // Return null for failed requests
        })
      );
      
      const responses = await Promise.all(promises);
      
      // Extract the blog data from each successful response
      const relatedBlogsData = responses
        .filter(response => response && response.data && response.data.success) // Filter out failed requests
        .map(response => response.data.data)
        .filter(Boolean); // Remove any falsy values
      
      
      setRelatedBlogs(relatedBlogsData);
    } catch (err) {
      
      setRelatedBlogs([]); // Set to empty array on error
    }
  };

  if (loading) {
    // Render a skeleton-like placeholder that preserves vertical space
    return (
      <>
        <div className="container" style={{ minHeight: '65vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '3.5rem', paddingBottom: '3.5rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="sr-only">Loading...</span>
            </div>
            <div style={{ marginTop: '16px', color: '#666' }}>Loading article...</div>
          </div>
        </div>

        {/* Centered spinning logo (uses public/logo.png) */}
        <img src="/logo.png" alt="SkillSiksha" className="page-loading-logo" />

        <div style={{ height: '4rem' }}></div>
      </>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          Blog not found.
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Featured Image - Full width across entire screen */}
      {blog.featured_image && (
        <div style={{ 
          width: '100vw', 
          position: 'relative', 
          left: '50%', 
          right: '50%', 
          marginLeft: '-50vw', 
          marginRight: '-50vw',
          marginBottom: '2rem'
        }}>
          <img
            src={getImageUrl(blog.featured_image)}
            alt={blog.title}
            style={{
              width: '100vw',
              height: 'auto',
              maxHeight: '500px',
              objectFit: 'cover',
              display: 'block'
            }}
          />
        </div>
      )}

      <div className="container pt-3 pt-md-5 pb-5">
        <div className="row">
          <div className="col-12">
            {/* Blog title */}
            <h2 className="mb-3">{blog.title}</h2>

            {/* Blog Content */}
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            {/* Table of Contents */}
            {blog.table_of_contents && (
              <div className="card mb-4 mt-5">
                <div className="card-header">
                  <h5 className="mb-0">Table of Contents</h5>
                </div>
                <div className="card-body">
                  <div
                    dangerouslySetInnerHTML={{ __html: blog.table_of_contents }}
                  />
                </div>
              </div>
            )}

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mt-4">
                <strong>Tags:</strong>
                {blog.tags.map((tag, index) => (
                  <span key={index} className="badge bg-secondary me-1">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Previous and Next Blog Navigation */}
            {(previousBlog || nextBlog) && (
              <div className="row mt-5">
                <div className="col-6">
                  {previousBlog && (
                    <Link
                      to={`/blog/${previousBlog.slug}`}
                      className="btn btn-outline-primary"
                    >
                      ← {previousBlog.title}
                    </Link>
                  )}
                </div>
                <div className="col-6 text-end">
                  {nextBlog && (
                    <Link
                      to={`/blog/${nextBlog.slug}`}
                      className="btn btn-outline-primary"
                    >
                      {nextBlog.title} →
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Related Posts */}
            <div className="mt-5 mb-5 pb-5" style={{ borderTop: '1px solid #eee', paddingTop: '2rem' }}>
              <h4 style={{ 
                textAlign: 'center', 
                marginBottom: '2rem', 
                color: '#333', 
                fontWeight: '600',
                position: 'relative'
              }}>
                Related Posts
                <span style={{ 
                  display: 'block', 
                  width: '50px', 
                  height: '3px', 
                  backgroundColor: '#ff9800', 
                  margin: '0.5rem auto',
                  borderRadius: '2px'
                }}></span>
              </h4>
              {relatedBlogs && relatedBlogs.length > 0 ? (
                <div className="blog-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '60px',
                  maxWidth: '1400px',
                  margin: '0 auto'
                }}>
                  {relatedBlogs.map((relatedBlog) => (
                    <article 
                      key={relatedBlog.id} 
                      className="insight-card"
                      style={{
                        backgroundColor: '#fff',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-10px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                      }}
                    >
                      {/* Image - Fixed height, full width */}
                      <div style={{ position: 'relative', height: '260px', overflow: 'hidden' }}>
                        <Link to={`/blog/${relatedBlog.slug}`}>
                          {relatedBlog.featured_image ? (
                            <img
                              src={getImageUrl(relatedBlog.featured_image)}
                              alt={relatedBlog.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.5s ease',
                                display: 'block'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/600x260/eeeeee/999999?text=No+Image';
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#aaa',
                              fontSize: '18px'
                            }}>
                              No Image Available
                            </div>
                          )}
                        </Link>
                      </div>

                      {/* Card Content - Takes remaining space */}
                      <div style={{
                        padding: '28px 24px',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {/* Title */}
                        <h2 style={{ margin: '0 0 14px 0', fontSize: '1.35rem', lineHeight: '1.3', fontWeight: '600' }}>
                          <Link
                            to={`/blog/${relatedBlog.slug}`}
                            style={{
                              color: '#212529',
                              textDecoration: 'none',
                              transition: 'color 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#ff9800'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#212529'}
                          >
                            {relatedBlog.title}
                          </Link>
                        </h2>

                        {/* Date */}
                        {(relatedBlog.published_at || relatedBlog.created_at) && (
                          <div style={{
                            color: '#666',
                            fontSize: '14px',
                            marginBottom: '20px'
                          }}>
                            {new Date(relatedBlog.published_at || relatedBlog.created_at).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        )}

                        {/* Read More - Pushed to bottom */}
                        <div style={{ marginTop: 'auto' }}>
                          <Link
                            to={`/blog/${relatedBlog.slug}`}
                            style={{
                              color: '#ff9800',
                              fontWeight: '600',
                              fontSize: '15px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.8px',
                              textDecoration: 'none',
                              transition: 'all 0.3s ease',
                              display: 'inline-block'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(8px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
                          >
                            Read More →
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p>No related posts found.</p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Spacer to create margin before footer */}
      <div style={{ height: '4rem' }}></div>
      
      {/* Responsive styles for related posts grid */}
      <style jsx>{`
        @media (max-width: 992px) {
          .blog-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .blog-grid {
            grid-template-columns: 1fr !important;
            gap: 40px;
          }
        }
      `}</style>
    </>
  );
};

export default BlogDetail;